import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OktaAuthService } from '@okta/okta-angular';
import * as OktaSignIn from '@okta/okta-signin-widget';

const COOKIE_NAME = {
    recoveryToken: 'recoveryToken',
    userToLogin: 'userToLogin',
    oktaRememberUsername: 'ln'
};

@Component({
	selector: 'login-form',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
	public widget: any;

	constructor(
		private readonly oktaAuth: OktaAuthService,
		private readonly router: Router,
        private http: HttpClient,
        private route: ActivatedRoute,
	) {
		this.widget = new OktaSignIn({
			baseUrl: window['__ccpEnv'].SSODomain,
			clientId: window['__ccpEnv'].SSOClientId,
			redirectUri: window['__ccpEnv'].uiDomain + '/sso.html',
			logo: 'https://www.amobee.com/wp-content/themes/amobee/assets/images/amobee_logo.svg',
			authParams: {
				responseType: ['id_token', 'token'],
				display: 'page',
				pkce: false,
				scopes: ['openid', 'email', 'profile'],
				tokenManager: {
					autoRenew: false,
					secure: true,
					storage: 'localStorage'
				}
			},
			colors: {
				brand: '#3bb0c9'
			},
			idpDiscovery: {
				requestContext: window.location.href
			},
			features: {
				idpDiscovery: true
			},
            transformUsername: (username, operation) => {
                this.setCookie(COOKIE_NAME.userToLogin, username);

                // Transform to avoid triggering for idp discovery
                if (operation === 'IDP_DISCOVERY') {
                    return 'fakeUsernameNotToTriggerIdpDiscovery';
                }
                return username;
            },
		});
	}

	public async ngOnInit() {
		this.oktaAuth
			.isAuthenticated()
			.then((authenticated) => {
				if (authenticated) {
					this.router.navigateByUrl('');
				}
				else {
                    const recoveryToken = this.route.snapshot.queryParamMap.get('recoveryToken');
                    if (recoveryToken) {
                        // Set recovery token in cookie for sso.html
                        let expireInSecondsFromNow = 1800;
                        let expireIn = new Date();
                        expireIn.setTime(expireIn.getTime() + (expireInSecondsFromNow * 1000));
                        this.setCookie(COOKIE_NAME.recoveryToken, recoveryToken, expireIn, '/');
                    } else if (window['__ccpEnv'].SSOPortalEnabled) {
                        window.location = window['__ccpEnv'].SSOPortalHostname + window['__ccpEnv'].SSOPortalLoginUri;
                    } else {
                        this.renderWidget();
                    }
				}
			})
			.catch((err) => {
				console.error(err);
			});
	}

    private rememberUsername(username) {
        let expireIn = new Date();
        expireIn.setFullYear(expireIn.getFullYear() + 1);
        this.setCookie(COOKIE_NAME.oktaRememberUsername, username, expireIn);
    }

    private checkLoggedInUser() {
        const loggedInAs = this.route.snapshot.queryParamMap.get('loggedInAs');
        const userToLogin = this.getCookie(COOKIE_NAME.userToLogin);
        if (loggedInAs && loggedInAs !== userToLogin) {
            // Add custom error message if logged in as different user
            this.addCustomErrorMessage(`
                <div id="customErrorMessage"><div class="okta-form-infobox-error infobox infobox-error" role="alert">
                    <span class="icon error-16"></span>
                    <p>You are currently logged in as '${loggedInAs}' with your Single Sign-On instance instead of the expected user '${userToLogin}'. Please log out of your Single Sign-On instance before attempting to log in again.</p>
                </div></div>
                `.trim())
        }

        // Clear the param after checking
        this.router.navigate([], {
            queryParams: {'loggedInAs': null},
            queryParamsHandling: 'merge'
        })
            .then()
            .catch();
    }

    private afterRenderPrimaryAuth() {
        this.checkLoggedInUser();

        const username = (<HTMLInputElement> document.getElementById('okta-signin-username')).value;
        const cookieUsername = this.getCookie(COOKIE_NAME.oktaRememberUsername);
        if (cookieUsername) {
            // Re-check remember me box if stored in cookie
            const rememberLabel = (<HTMLElement> document.getElementsByName('remember')[0].nextSibling);
            if (!rememberLabel.className.includes('checked')) {
                rememberLabel.click();
                // Refocus on password
                (<HTMLInputElement> document.getElementsByName('password')[0]).focus();
            }

            // Fix the username stored in cookie
            if (cookieUsername !== username) {
                this.rememberUsername(username);
            }
        }

        const submitButton = document.getElementById('okta-signin-submit');
        submitButton && submitButton.addEventListener('click', () => {
            this.clearCustomErrorMessage();
        });
    }

    private afterRenderIdpDiscovery() {
        this.checkLoggedInUser();

        let checkedIdp = false;
        const submitButton = document.getElementById('idp-discovery-submit');
        submitButton && submitButton.addEventListener('click', event => {
            this.clearCustomErrorMessage();
            const usernameInput = document.getElementById('idp-discovery-username');
            const username = (<HTMLInputElement> usernameInput).value;
            if (!username) {
                return;
            }

            if (checkedIdp) {
                const loginForm = document.getElementsByTagName('form')[0];
                loginForm.className = loginForm.className.replace(' o-form-disabled', '');
                return;
            }
            event.preventDefault();
            event.stopPropagation();

            // Disable form
            const loginForm = document.getElementsByTagName('form')[0];
            loginForm.className += ' o-form-disabled';
            submitButton.setAttribute('disabled', 'true');

            // Disable input
            usernameInput.setAttribute('disabled', 'true');
            usernameInput.parentElement.className += ' o-form-disabled';

            // Add loading icon
            const beaconContainer = document.getElementsByClassName('beacon-container')[0];
            beaconContainer.className += ' beacon-small beacon-loading';
            beaconContainer.setAttribute('style', 'transform: scale(1, 1); text-indent: 1px;');
            const bgHelper = document.createElement('div');
            bgHelper.className = 'bg-helper auth-beacon auth-beacon-security';
            bgHelper.setAttribute('data-se', 'loading-beacon');
            const beaconBorder = document.createElement('div');
            beaconBorder.className = 'okta-sign-in-beacon-border auth-beacon-border js-auth-beacon-border';
            bgHelper.appendChild(beaconBorder);
            beaconContainer.appendChild(bgHelper);

            const requestOptions = {
                headers: {
                    'Sso-App-Context': window['__ccpEnv'].appAuthorityName,
                    'Skip-Auth-Interceptor': ''
                }
            };

            return this.http.get(`${window['__ccpEnv'].SSOManagerUrl}/users/check/${username}`, requestOptions)
                .toPromise()
                .then(() => {
                    return this.http.get(`${window['__ccpEnv'].SSOManagerUrl}/users/${username}/idp`, requestOptions)
                        .toPromise()
                        .then(({data}: any) => {
                            if (!data || !data.length || !data[0] || !data[0].idpId) {
                                return Promise.reject();
                            }

                            this.setCookie(COOKIE_NAME.userToLogin, username);

                            // Cache username if remember is checked
                            const rememberLabel = (<HTMLElement> document.getElementsByName('remember')[0].nextSibling);
                            if (rememberLabel.className.includes('checked')) {
                                this.rememberUsername(username);
                            }

                            this.widget.authClient.token.getWithRedirect(
                                {
                                    scopes: [
                                        'openid',
                                        'email',
                                        'profile'
                                    ],
                                    idp: data[0].idpId,
                                    responseType: ['id_token', 'token']
                                });
                        });
                })
                .catch((err) => {
                    checkedIdp = true;
                    submitButton.removeAttribute('disabled');
                    return submitButton.click();
                });
        });
    }

    private renderWidget() {
		this.widget.renderEl(
			{
				el: '#okta-signin-container'
			},
			(res) => {
                /**
                 * In this flow, the success handler will not be called because we redirect
                 * to the Okta org for the authentication workflow.
                 */
			},
			(err) => {
				console.log(err);
				throw err;
			}
		);
        this.widget.on('afterRender', context => {
            switch (context.controller) {
                case 'primary-auth':
                    this.afterRenderPrimaryAuth();
                    break;
                case 'idp-discovery':
                    this.afterRenderIdpDiscovery();
                    break;
            }
        });
	}

    private addCustomErrorMessage(errorMessage) {
        document.getElementsByClassName('o-form-error-container')[0].innerHTML = errorMessage;
    }

    private clearCustomErrorMessage() {
        const customErrorMessage = document.getElementById('customErrorMessage');
        customErrorMessage && document.getElementsByClassName('o-form-error-container')[0].removeChild(customErrorMessage);
    }

    private getCookie(name: string) {
        var v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
        return v ? v[2] : null;
    }

    private setCookie (name: string, value: string, expires?: Date, path?: string, domain?: string) {
        document.cookie = name + "=" + encodeURI(value) +
            ((expires) ? "; expires=" + expires.toUTCString() : "") +
            ((path) ? "; path=" + path : "") +
            ((domain) ? "; domain=" + domain : "") +
            "; SameSite=None; Secure";
    }
}
